/**
 * UI 문구 + 언어 결정.
 *
 * 문구를 번들에 넣는다(정적 파일로 받지 않는다). 이유:
 * - **키를 타입으로 뽑을 수 있다.** `t('pick.saev')` 같은 오타가 컴파일에서 걸린다
 * - 언어별로 코드 분할되어 필요한 것만 내려간다
 * - 문구와 그걸 쓰는 코드가 같이 배포되므로 어긋날 수 없다
 *
 * 카테고리는 반대다 — 앱 배포와 무관하게 갱신되고 용량이 커서 정적 파일로 받는다.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  FALLBACK_LANGUAGE,
  isSupportedLanguage,
  LANGUAGE_TAGS,
  parseLocalizedPath,
  resolveLanguage,
  SITE_COPY,
  type Language,
} from '@lifetimeline/shared';

import { ko, type Messages } from './ko.js';

/** `common.save` 같은 점 표기 키. `Messages` 구조에서 자동으로 뽑는다. */
type MessageKey<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : MessageKey<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

export type TranslationKey = MessageKey<Messages>;

const LANGUAGE_STORAGE_KEY = 'lifetimeline.language';

/**
 * 같은 값을 **쿠키로도** 둔다. 읽는 쪽은 CloudFront 함수다 — 접두어 없는
 * 주소(`/`, `/privacy`)로 들어온 사람을 `/ko`·`/en` 중 어디로 보낼지 정한다
 * (`infra/web.yaml`). LocalStorage는 서버가 못 본다.
 *
 * ⚠️ 이름은 `infra/web.yaml`의 함수와 **같아야 한다.**
 */
const LANGUAGE_COOKIE = 'lang';

/**
 * 언어별 문구는 필요할 때 불러온다. 한국어는 기본이라 처음부터 들고 있고,
 * 다른 언어는 고를 때 받아온다.
 */
const loaders: Record<Language, () => Promise<Messages>> = {
  ko: async () => ko,
  en: async () => (await import('./en.js')).en,
  ja: async () => (await import('./ja.js')).ja,
  'zh-cn': async () => (await import('./zh-cn.js')).zhCn,
  'zh-tw': async () => (await import('./zh-tw.js')).zhTw,
  es: async () => (await import('./es.js')).es,
  pt: async () => (await import('./pt.js')).pt,
};

/** 저장된 언어 선택. 읽기가 실패해도 앱이 죽지 않게 감싼다. */
export function readStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored !== null && isSupportedLanguage(stored) ? stored : null;
  } catch {
    // 시크릿 창이나 저장소 차단 상태
    return null;
  }
}

export function storeLanguage(language: Language): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // 저장이 안 돼도 이번 세션에는 적용된다
  }
  document.cookie = `${LANGUAGE_COOKIE}=${language}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

/**
 * 언어를 정한다. 앞의 것이 이긴다:
 *
 *   ⓪ 주소 접두어(/ko, /en/privacy)  언어별 화면은 주소가 곧 언어다
 *   ① URL 쿼리(?lang=en)   공유·디버깅용
 *   ② 사용자 프로필         로그인 상태, 기기 간 유지
 *   ③ LocalStorage         비로그인·즉시 반영
 *   ④ 브라우저 설정
 *   ⑤ 기본값(en)
 *
 * ②보다 ①이 앞서는 이유: 링크로 언어를 지정해 보낼 수 있어야 한다.
 *
 * ⓪이 맨 앞인 이유: 검색 결과에서 `/ko`를 눌렀는데 영어가 뜨면 검색엔진이
 * 그 주소를 한국어 문서로 믿을 수 없다. 주소와 화면의 언어가 늘 같아야 한다.
 */
export function decideLanguage(profileLanguage?: Language | null): Language {
  const fromPath = parseLocalizedPath(window.location.pathname);
  if (fromPath !== null) {
    return fromPath.language;
  }
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  if (fromQuery !== null && isSupportedLanguage(fromQuery)) {
    return fromQuery;
  }
  if (profileLanguage !== null && profileLanguage !== undefined) {
    return profileLanguage;
  }
  const stored = readStoredLanguage();
  if (stored !== null) {
    return stored;
  }
  return resolveLanguage(navigator.language);
}

export type Translate = (key: TranslationKey, values?: Record<string, string | number>) => string;

interface I18nValue {
  language: Language;
  /**
   * 기본은 **저장한다**(사람이 고른 것). `persist: false`는 주소가 정한
   * 언어다 — 검색 결과에서 `/en`을 한 번 눌렀다고 그 사람의 언어를 영어로
   * 바꿔 버리면 안 된다.
   */
  setLanguage: (language: Language, options?: { persist?: boolean }) => void;
  t: Translate;
  /** 문구를 아직 못 받은 상태 */
  loading: boolean;
}

const I18nContext = createContext<I18nValue | null>(null);

function lookup(messages: Messages, key: string): string {
  const value = key
    .split('.')
    .reduce<unknown>(
      (node, part) =>
        typeof node === 'object' && node !== null
          ? (node as Record<string, unknown>)[part]
          : undefined,
      messages,
    );

  // 키가 틀리면 키 자체를 보여준다 — 빈 칸보다 원인을 찾기 쉽다.
  // 타입이 막아주므로 정상적으로는 도달하지 않는다.
  return typeof value === 'string' ? value : key;
}

/** `{name}` 자리를 값으로 바꾼다. 값이 없으면 자리표시자를 그대로 둔다. */
function interpolate(template: string, values?: Record<string, string | number>): string {
  if (values === undefined) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

export function I18nProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode;
  initialLanguage?: Language;
}) {
  const [language, setLanguageState] = useState<Language>(initialLanguage ?? decideLanguage());
  const [messages, setMessages] = useState<Messages>(ko);
  const [loading, setLoading] = useState(language !== 'ko');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    loaders[language]()
      .then((loaded) => {
        if (!cancelled) {
          setMessages(loaded);
        }
      })
      .catch(() => {
        // 문구를 못 받으면 기본 언어로라도 보여준다. 빈 화면보다 낫다.
        if (!cancelled) {
          setMessages(ko);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  /*
   * 문서 언어와 탭 제목. **레이아웃 효과**인 이유: 화면들이 자기 제목
   * (프로필의 사람 이름 등)을 일반 효과로 얹는데, 효과는 자식부터 돌아서
   * 여기를 일반 효과로 두면 부모인 이쪽이 나중에 덮어쓴다. 레이아웃 효과는
   * 일반 효과보다 늘 먼저 돈다.
   */
  useLayoutEffect(() => {
    document.documentElement.lang = LANGUAGE_TAGS[language];
    document.title = SITE_COPY[language].title;
  }, [language]);

  const setLanguage = useCallback((next: Language, options?: { persist?: boolean }) => {
    setLanguageState(next);
    if (options?.persist !== false) {
      storeLanguage(next);
    }
  }, []);

  const t = useCallback<Translate>(
    (key, values) => interpolate(lookup(messages, key), values),
    [messages],
  );

  const value = useMemo<I18nValue>(
    () => ({ language, setLanguage, t, loading }),
    [language, setLanguage, t, loading],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) {
    throw new Error('I18nProvider 안에서만 쓸 수 있습니다');
  }
  return value;
}

export { FALLBACK_LANGUAGE };
export type { Language };
